'use client';

import { type LandingTheme, type ContentBoxStyle } from '@fitvent/types';
import { V2BackgroundEffect, type BackgroundEffectType } from '@/components/public/v2/V2BackgroundEffect';
import type { EffectContext } from '@/lib/public/effects/context';
import { fontFormatHint } from '@/lib/utils/font-format';
import { safeCssColor, safeCssColors, safeCssFontFamily, safeCssFontName } from '@/lib/utils/safe-css';
import { deriveThemeColours, isDarkSurface, withAlpha } from '@/lib/public/theme-colours';
import { googleFontsCss2Url, headingWeightDrawing, uploadedFontWeightRange } from '@/lib/fonts/heading-weights';
import { cornerRadiiOf, cornerRadiusCss } from '@/lib/public/corner-style';

/**
 * The outline that makes up a heading weight the font lacks (see
 * headingWeightDrawing). Painted under the fill so letters thicken outward and
 * counters stay open; the browser is told not to fake a bold on top. Each
 * letter grows by half the outline on either side, so the gaps between them
 * close by one outline width: the same again as letter spacing puts them back.
 */
const HEADING_STROKE_CSS = '-webkit-text-stroke: var(--fv-heading-stroke) currentColor; paint-order: stroke fill; font-synthesis-weight: none; letter-spacing: var(--fv-heading-stroke);';

interface ThemedEventPageProps {
  children: React.ReactNode;
  theme: LandingTheme;
  organizerName?: string;
  organizerLogo?: string | null;
  backgroundEffect?: BackgroundEffectType;
  /** What the background effect draws from: Topo's seed, Outline type's word, the watermark's placement. */
  effectContext?: EffectContext;
  hasMobileTicketBar?: boolean;
  /** When false, hides the "Powered by Fitvent" header link and footer. Used
   * by /demo pages embedded in the landing site (the brand showcase wants
   * the page to read as the organiser's, not as a Fitvent demo). */
  showPoweredBy?: boolean;
  headerBranding?: {
    showLogo?: boolean;
    showName?: boolean;
    customName?: string;
    customLogoUrl?: string;
  };
  uploadedFonts?: {
    /**
     * sizeAdjust scales the glyphs inside the em box, as a percentage. Display
     * faces often have a small x-height, so they read noticeably smaller than a
     * text face at the same px size (Crackhouse at 20px next to Cabin at 20px).
     * Adjusting this rather than the font-size keeps every layout, line height
     * and breakpoint untouched and only changes how large the letters draw.
     * Unset means 100%, i.e. exactly today's behaviour.
     *
     * weights are the ones the file really has, read at upload (absent on
     * older uploads): headings snap to them and the face is declared to them.
     */
    heading?: { name: string; url: string; sizeAdjust?: number; weights?: number[] };
    body?: { name: string; url: string; sizeAdjust?: number; weights?: number[] };
    brand?: { name: string; url: string; sizeAdjust?: number; weights?: number[] };
  };
}

export function ThemedEventPage({ children, theme, organizerName, organizerLogo, backgroundEffect = 'none', effectContext, hasMobileTicketBar = false, showPoweredBy = true, headerBranding, uploadedFonts }: ThemedEventPageProps) {
  const { fontStyle, colors: rawColors, customFonts, borderRadius = 'rounded', headerWeight: savedHeaderWeight = 'bold', brandNameFont: rawBrandNameFont, contentBoxStyle = 'white', cardBorders, cardBorderColour, boxFill } = theme;

  /**
   * These values are interpolated into the <style> block below, and they come
   * from events.metadata.landingThemeCustomColors, which the landing-theme
   * route writes straight from the request body. Unsanitised, a colour of
   * `red; } body::before { ... }` closes the declaration and appends arbitrary
   * rules to this public page.
   *
   * Sanitised once, here, so all 61 interpolations downstream inherit the
   * guarantee and a new one cannot forget it.
   */
  // An empty optional colour means "not set" (headings follow the text), not
  // "invalid": dropped before sanitising so it can't become the black fallback.
  const colors = safeCssColors(
    Object.fromEntries(Object.entries(rawColors ?? {}).filter(([, v]) => v !== '' && v != null)) as typeof rawColors,
    '#000000',
  );
  // Every surface and ink, each checked against what it sits on.
  // Card outlines: the derived hairline, none, or the organiser's colour
  // (sanitised like every other colour that reaches the <style> block).
  // Box fill 'none' takes the fill off section boxes; anything else is filled.
  const derived = deriveThemeColours(colors, contentBoxStyle, {
    style: cardBorders === 'none' ? 'none' : 'subtle',
    colour: safeCssColor(cardBorderColour, '') || undefined,
    boxFill: boxFill === 'none' ? 'none' : 'filled',
  });
  const brandNameFont = safeCssFontFamily(rawBrandNameFont, '');

  // Headings draw at a weight the heading font really has: one it lacks is
  // faked by the browser (letters drawn twice on iPhone). When the saved
  // weight is heavier than the font goes, an outline in the heading's own
  // colour makes up the rest, so Bold on a Regular-only font still reads bold.
  const headerWeight = savedHeaderWeight;
  const { css: headingCss, strokeEm: headingStrokeEm } = headingWeightDrawing({
    fontStyle,
    headingFont: uploadedFonts?.heading?.name ?? customFonts?.heading,
    uploadedHeading: fontStyle === 'custom' ? uploadedFonts?.heading : undefined,
    saved: savedHeaderWeight,
  });
  // Google stylesheets, asking only for weights each family has.
  const headingFontHref = fontStyle === 'custom' && customFonts?.heading && !uploadedFonts?.heading ? googleFontsCss2Url(customFonts.heading, [400, 700, headingCss]) : null;
  const bodyFontHref = fontStyle === 'custom' && customFonts?.body && !uploadedFonts?.body ? googleFontsCss2Url(customFonts.body, [400, 700]) : null;
  const brandFontHref = brandNameFont && !uploadedFonts?.brand ? googleFontsCss2Url(brandNameFont, [400, 700, headingCss]) : null;

  // Default to showing both if not specified
  const showLogo = headerBranding?.showLogo ?? true;
  const showName = headerBranding?.showName ?? true;

  // Use custom values if provided, otherwise fall back to organizer profile
  const displayName = headerBranding?.customName || organizerName || '';
  const displayLogo = headerBranding?.customLogoUrl || organizerLogo || '';

  // Corners: Rounded, Soft square or Square (lib/public/corner-style.ts).
  // Every surface draws from the --fv-radius-* variables; --border-radius
  // (older surfaces) follows the controls.
  const radii = cornerRadiiOf(borderRadius);
  const borderRadiusValue = radii.control;

  // Light or dark is measured on the page itself, never inferred from the
  // text colour (a mid-tone orange text read as "dark" and flipped surfaces).
  const pageIsDark = isDarkSurface(colors.primary);

  // Ensure Fitvent branding always stands out from background
  const fitventBrandingColor = pageIsDark ? '#FFFFFF' : '#111827';

  // Content box colours (white, black or see-through), from the derivation.
  const cardStyles = derived.card;

  // Compute numeric heading weight for CSS variable
  const getNumericHeaderWeight = (): string => String(headingCss);

  // Determine font classes based on style
  const getFontClass = () => {
    if (fontStyle === 'custom' && customFonts?.body) {
      return '';
    }
    switch (fontStyle) {
      case 'modern':
        return 'font-[family-name:var(--font-dm-sans)]';
      case 'minimal':
        return 'font-[family-name:var(--font-inter)]';
      case 'bold':
        return 'font-[family-name:var(--font-geist-sans)]';
      case 'classic':
        return '';
      default:
        return '';
    }
  };

  const getHeadingFontClass = () => {
    // Only use uploaded/custom fonts when fontStyle is 'custom'
    if (fontStyle === 'custom') {
      if (uploadedFonts?.heading) {
        return `'${safeCssFontName(uploadedFonts.heading.name, 'sans-serif')}', sans-serif`;
      }
      if (customFonts?.heading) {
        return `'${safeCssFontName(customFonts.heading, 'sans-serif')}', sans-serif`;
      }
    }
    switch (fontStyle) {
      case 'modern':
        return 'var(--font-caprasimo)';
      case 'bold':
        return 'var(--font-bebas-neue)';
      case 'minimal':
        return 'var(--font-inter)';
      case 'classic':
        return 'Arial, Helvetica, sans-serif';
      default:
        return '';
    }
  };

  const getHeadingWeight = () => {
    // Use custom header weight if specified
    // (snapped to the weights the heading font has, above)
    if (headerWeight) {
      return `font-weight: ${headingCss};`;
    }

    // Fallback to font style defaults
    if (fontStyle === 'custom') {
      return 'font-weight: 700;';
    }
    // Add explicit bold for classic and minimal
    switch (fontStyle) {
      case 'classic':
      case 'minimal':
        return 'font-weight: 700;';
      case 'bold':
        return 'font-weight: 600;';
      default:
        return '';
    }
  };

  const getBodyFontFamily = () => {
    // Only use uploaded/custom fonts when fontStyle is 'custom'
    if (fontStyle === 'custom') {
      if (uploadedFonts?.body) {
        return `'${safeCssFontName(uploadedFonts.body.name, 'sans-serif')}', sans-serif`;
      }
      if (customFonts?.body) {
        return `'${safeCssFontName(customFonts.body, 'sans-serif')}', sans-serif`;
      }
    }
    return '';
  };

  return (
    <>
      {/* Load custom Google Fonts if specified (only if not using uploaded fonts) */}
      {headingFontHref && <link href={headingFontHref} rel="stylesheet" />}
      {bodyFontHref && <link href={bodyFontHref} rel="stylesheet" />}
      {brandFontHref && <link href={brandFontHref} rel="stylesheet" />}

      {/* Load uploaded custom fonts via @font-face.
          Two iOS Safari gotchas, both fixed here:
          1. format() hint - without it iOS can refuse to load the file and
             silently fall back to a system font (a TTF/woff2 that rendered on
             desktop didn't pull through on iPhone). Derived from the extension.
          2. font-weight RANGE not `normal`. Uploaded fonts are a single
             master, but headings render at font-weight: 700. iOS WebKit
             would not match a 700 request against a point-declared 400 face
             and fell back to a system font; a range keeps the face matching
             at every requested weight.
          The range deliberately stops at 500, NOT 900: a face that claims to
          cover 700 is used as-is with no synthetic bold, so a Light/Regular
          master rendered every bold element thin (the washed-out Get Tickets
          bar on fonts like Brutal Type Light). Capping the declared range at
          500 keeps the face matched (iOS safe) while letting the browser
          synthesise the missing bold weights. A heavier master (read at
          upload) is declared up to its own weight, so it is never emboldened
          again on top; a variable font across its whole axis. */}
      {((fontStyle === 'custom' && (uploadedFonts?.heading || uploadedFonts?.body)) || uploadedFonts?.brand) && (
        <style>{`
          ${fontStyle === 'custom' && uploadedFonts?.heading ? `
            @font-face {
              font-family: '${uploadedFonts.heading.name}';
              src: url('${uploadedFonts.heading.url}')${fontFormatHint(uploadedFonts.heading.url)};
              font-weight: ${uploadedFontWeightRange(uploadedFonts.heading.weights)};
              font-style: normal;
              font-display: swap;
              ${uploadedFonts.heading.sizeAdjust ? `size-adjust: ${uploadedFonts.heading.sizeAdjust}%;` : ''}
            }
          ` : ''}
          ${fontStyle === 'custom' && uploadedFonts?.body ? `
            @font-face {
              font-family: '${uploadedFonts.body.name}';
              src: url('${uploadedFonts.body.url}')${fontFormatHint(uploadedFonts.body.url)};
              font-weight: ${uploadedFontWeightRange(uploadedFonts.body.weights)};
              font-style: normal;
              font-display: swap;
              ${uploadedFonts.body.sizeAdjust ? `size-adjust: ${uploadedFonts.body.sizeAdjust}%;` : ''}
            }
          ` : ''}
          ${uploadedFonts?.brand ? `
            @font-face {
              font-family: '${uploadedFonts.brand.name}';
              src: url('${uploadedFonts.brand.url}')${fontFormatHint(uploadedFonts.brand.url)};
              font-weight: ${uploadedFontWeightRange(uploadedFonts.brand.weights)};
              font-style: normal;
              font-display: swap;
              ${uploadedFonts.brand.sizeAdjust ? `size-adjust: ${uploadedFonts.brand.sizeAdjust}%;` : ''}
            }
          ` : ''}
        `}</style>
      )}

      <div
        suppressHydrationWarning
        className={`min-h-screen ${getFontClass()}`}
        style={{
          position: 'relative',
          color: derived.text,
          ['--theme-secondary' as any]: colors.secondary,
          ['--button-text' as any]: derived.onAccent,
          ['--border-radius' as any]: borderRadiusValue,
          ...(fontStyle === 'custom' && customFonts?.body && { fontFamily: getBodyFontFamily() }),
          ...(fontStyle === 'classic' && { fontFamily: 'Arial, Helvetica, sans-serif' }),
        }}
      >
      {/* Background effect - absolute positioned inside this relative wrapper */}
      <V2BackgroundEffect effect={backgroundEffect} logoUrl={displayLogo || null} page={colors.primary} tint={derived.text} accent={colors.secondary} {...effectContext} />

      <style dangerouslySetInnerHTML={{ __html: `
        body { background-color: ${colors.primary}; }
        /* Vars also live on <body> so themed content that portals out of the
           .themed-page subtree (FvSelect menus, dialogs, toasts) still inherits
           the organiser theme instead of falling back to dashboard defaults. */
        .themed-page, body {
          --fv-heading-font: ${getHeadingFontClass() || 'inherit'};
          --fv-body-font: ${getBodyFontFamily() || 'inherit'};
          --fv-heading-weight: ${getNumericHeaderWeight()};
          --fv-heading-stroke: ${headingStrokeEm}em;
          ${cornerRadiusCss(borderRadius)}
          --border-radius: ${borderRadiusValue};
          --fv-text: ${cardStyles.text};
          --fv-text-primary: ${cardStyles.text};
          --fv-text-secondary: ${cardStyles.muted};
          --fv-text-muted: ${cardStyles.muted};
          --fv-text-tertiary: ${cardStyles.muted};
          --fv-hover: ${derived.bgSecondary};
          --fv-hover-strong: ${derived.bgSecondary};
          --fv-border: ${cardStyles.border};
          --fv-border-strong: ${cardStyles.borderStrong};
          --ticket-border: ${cardStyles.ticketBorder};
          --fv-border-hover: ${cardStyles.border};
          /* The page's own ground, as opposed to --fv-bg (the content box,
             which is literally transparent for the transparent box style).
             Anything pinned over the page (a fixed action bar) needs this to
             sit on, and needs it to follow the organiser's theme rather than
             assuming dark. */
          --fv-page-bg: ${colors.primary};
          --fv-bg: ${cardStyles.bg};
          --fv-bg-secondary: ${derived.bgSecondary};
          --fv-bg-input: ${cardStyles.bg};
          --fv-bg-card: ${cardStyles.bg};
          /* Raised surfaces and menus follow the PAGE: lifted on a dark page,
             white on a light one. */
          --fv-bg-raised: ${derived.raised};
          --fv-bg-overlay: ${derived.overlay};
          --fv-accent: ${colors.secondary};
          --fv-accent-border: ${derived.accentBorder};
          --fv-accent-light: ${derived.accentLight};
          --fv-accent-bg: ${derived.accentBg};
          --fv-accent-text: ${derived.onAccent};
          --fv-accent-ink: ${derived.accentInk};
          --fv-theme-text: ${derived.text};
          --fv-theme-heading: ${derived.heading};
          --fv-theme-text-muted: ${colors.textMuted ? derived.textMuted : withAlpha(derived.text, 0.6)};
          /* Text on pills and other raised chips. */
          --fv-chip-text: ${derived.chipText};
          /* A disabled button: a solid quiet fill with a label that still reads. */
          --fv-cta-disabled-bg: ${derived.disabled.bg};
          --fv-cta-disabled-text: ${derived.disabled.text};
          --fv-cta-disabled-border: ${derived.disabled.border};
          ${derived.sheetBorder ? `--fv-sheet-border: ${derived.sheetBorder};` : ''}
          ${derived.rowFill ? `
          /* Borders off: rows told apart by an outline alone take a quiet
             tint instead, and sub-cards inside a section drop the inset they
             only needed to clear their outline, so their text lines up with
             the section heading. Unset with borders on: those rows keep
             their outline and padding exactly as before. */
          --fv-row-bg: ${derived.rowFill};
          --fv-subcard-pad-x: 0px;
          --fv-subcard-gap: 20px;` : ''}
        }
        /* Plain Tailwind outlines (.border, .border-t, divide-y with no colour
           of their own) are drawn in --border, which globals.css declares on
           :root as the DASHBOARD hairline, resolved there. So they ignored the
           organiser's theme and followed the visitor's light or dark setting.
           Re-declared here it resolves against this page's --fv-border, so
           those outlines follow Borders (Subtle, None, or a colour). Only on
           .themed-page, never body: this page is also embedded in other pages. */
        .themed-page { --border: var(--fv-border); }
        ${boxFill === 'none' && cardBorders === 'none' ? `
        /* The flat look (no fill, no outline): a section box is only its
           heading and content, so it lines up with the column edge like the
           organiser's own site, instead of keeping an inset that only made
           sense inside a box. Its vertical padding stays as spacing. */
        .themed-page .card-bg { padding-left: 0; padding-right: 0; }
        ` : ''}
        ${cardStyles.inputBorder !== cardStyles.border ? `
        /* Borders are off (or a faint custom colour): cards lose their outline,
           but inputs, selects and checkboxes keep a visible edge. Re-pointing
           the token on the control itself leaves error and focus colours,
           which set the border directly, untouched. */
        .themed-page input, .themed-page textarea, .themed-page select,
        .themed-page [aria-haspopup="listbox"], .themed-page [role="combobox"],
        .themed-page [role="checkbox"], .themed-page [data-slot="checkbox"],
        .themed-page [data-slot="select-trigger"] { --fv-border: ${cardStyles.inputBorder}; --border: ${cardStyles.inputBorder}; }
        ` : ''}
        /* A label inside a pill or a small numbered badge, centred on its
           letters rather than on the font's line box: some faces (serifs
           especially) sit their glyphs high, so the text floated above the
           middle. text-box trims the space above the capitals and below the
           baseline where the browser supports it; elsewhere line-height 1
           still tightens it. */
        .themed-page .fv-trim { display: block; line-height: 1; text-box: trim-both cap alphabetic; }
        .themed-page h1,
        .themed-page h2,
        .themed-page h3 {
          ${getHeadingFontClass() ? `font-family: ${getHeadingFontClass()} !important;` : ''}
          ${getHeadingWeight().replace(';', ' !important;')}
          color: ${derived.heading};
          ${headingStrokeEm > 0 ? HEADING_STROKE_CSS : ''}
        }
        .themed-page .card-bg h1 { color: ${cardStyles.heading}; }
        ${fontStyle === 'custom' && customFonts?.body ? `.themed-page { font-family: ${getBodyFontFamily()}; }` : ''}
        .themed-page .text-muted { color: ${colors.textMuted ? derived.textMuted : withAlpha(derived.text, 0.6)} !important; }
        .themed-page .btn-primary {
          background-color: ${colors.secondary};
          border-color: ${colors.secondary};
          color: ${derived.onAccent};
          border-radius: var(--fv-radius-button);
          display: flex; align-items: center; justify-content: center; text-align: center;
          transition: filter 0.1s ease, transform 0.1s ease, opacity 0.15s ease;
        }
        .themed-page .btn-primary.rounded-full { border-radius: var(--fv-radius-button); }
        .themed-page .btn-primary:hover { filter: brightness(0.9); }
        .themed-page .btn-primary:active { filter: brightness(0.75); transform: scale(0.98); }
        .themed-page .btn-primary.is-navigating { pointer-events: none; opacity: 0.7; filter: brightness(0.8); }
        /* fv-card hover uses a hardcoded teal shadow + un-themed accent border in
           globals.css; re-point it at the organiser accent on themed pages. */
        .themed-page a.fv-card:hover, .themed-page button.fv-card:hover {
          border-color: ${colors.secondary} !important;
          box-shadow: 0 2px 16px ${colors.secondary}22 !important;
        }
        .themed-page .border-themed { border-color: ${colors.secondary} !important; }
        .themed-page .bg-themed-secondary { background-color: ${colors.secondary}; }
        .themed-page .text-themed-secondary { color: ${derived.accentInk}; }
        .themed-page input[type="radio"]:checked { accent-color: ${colors.secondary}; }
        .themed-page input[type="checkbox"] { accent-color: ${colors.secondary}; }
        .themed-page .card-bg {
          background-color: ${cardStyles.bg}; color: ${cardStyles.text};
          border-radius: var(--fv-radius-card); border-color: ${cardStyles.border};
        }
        .themed-page .card-bg h2, .themed-page .card-bg h3 { color: ${cardStyles.heading}; }
        .themed-page .card-bg .text-muted { color: ${cardStyles.muted} !important; }
        .themed-page .card-bg.border, .themed-page .border.card-bg { border: 1px solid ${cardStyles.border}; }
        .themed-page .card-bg input[type="text"],
        .themed-page .card-bg input[type="email"],
        .themed-page .card-bg input[type="number"],
        .themed-page .card-bg input[type="date"],
        .themed-page .card-bg input[type="tel"],
        .themed-page .card-bg textarea,
        .themed-page .card-bg select {
          border-color: ${cardStyles.inputBorder}; color: ${cardStyles.text}; background: transparent;
        }
        .themed-page .card-bg input::placeholder,
        .themed-page .card-bg textarea::placeholder { color: ${cardStyles.muted}; }
        /* Tailwind's radius classes follow the corner style: small ones are
           controls, larger ones are boxes. rounded-full is left alone, since
           it also draws circles (avatars, dots, spinners); buttons and chips
           that use it take --fv-radius-button or --fv-radius-chip directly. */
        .themed-page .rounded-md, .themed-page .rounded-lg, .themed-page .rounded { border-radius: var(--fv-radius-control); }
        .themed-page .rounded-xl, .themed-page .rounded-2xl, .themed-page .rounded-3xl { border-radius: var(--fv-radius-card); }
        .themed-page .card-bg .rounded-xl, .themed-page .card-bg .rounded-2xl { border-radius: var(--fv-radius-tile); }
        .themed-page .rounded-t-xl, .themed-page .rounded-t-2xl, .themed-page .rounded-t-3xl { border-top-left-radius: var(--fv-radius-card); border-top-right-radius: var(--fv-radius-card); }
        .themed-page .rounded-b-xl, .themed-page .rounded-b-2xl { border-bottom-left-radius: var(--fv-radius-card); border-bottom-right-radius: var(--fv-radius-card); }
        .themed-page .rounded-t-lg, .themed-page .rounded-t-md { border-top-left-radius: var(--fv-radius-control); border-top-right-radius: var(--fv-radius-control); }
        .themed-page .rounded-b-lg, .themed-page .rounded-b-md { border-bottom-left-radius: var(--fv-radius-control); border-bottom-right-radius: var(--fv-radius-control); }
        /* A section box is a box whatever radius class it carries. */
        .themed-page .card-bg:not(.rounded-full) { border-radius: var(--fv-radius-card); }
        .themed-page header.border-b { border-bottom: 1px solid ${derived.pageHairline}; }
        .themed-page footer.border-t { border-top: 1px solid ${derived.pageHairline}; }
        .themed-page [data-slot="checkbox"][data-state="checked"] {
          background-color: var(--fv-accent) !important; border-color: var(--fv-accent) !important;
        }
        .themed-page [data-slot="button"][data-variant="outline"] {
          background: transparent; color: var(--fv-text-primary); border-color: var(--fv-border);
        }
        .themed-page [data-slot="button"][data-variant="outline"]:hover {
          background: var(--fv-accent-dim); color: var(--fv-text-primary);
        }
        .themed-page input:focus,
        .themed-page select:focus,
        .themed-page textarea:focus {
          outline: none;
          border-color: var(--fv-accent) !important;
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--fv-accent) 25%, transparent);
        }
      ` }} />

      <div className="themed-page" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <header className="border-b" style={{ borderColor: 'var(--fv-border)' }}>
          <div className="mx-auto max-w-7xl px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                {showLogo && displayLogo && (
                  <img onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    src={displayLogo}
                    alt={displayName || 'Organiser logo'}
                    className="h-8 md:h-10 w-auto max-w-[40vw] md:max-w-none object-contain shrink-0"
                  />
                )}
                {showName && displayName && (
                  <span
                    className={`text-lg md:text-xl truncate ${showLogo && displayLogo ? 'hidden md:inline' : ''}`}
                    style={{
                      fontWeight: getNumericHeaderWeight(),
                      color: 'var(--fv-theme-heading, var(--fv-theme-text))',
                      ...(uploadedFonts?.brand
                        ? { fontFamily: `'${uploadedFonts.brand.name}', sans-serif` }
                        : brandNameFont
                        ? { fontFamily: `'${brandNameFont}', sans-serif` }
                        : getHeadingFontClass()
                        ? { fontFamily: getHeadingFontClass() }
                        : {}),
                    }}
                  >
                    {displayName}
                  </span>
                )}
              </div>
              {showPoweredBy && (
                <span className="shrink-0 whitespace-nowrap text-xs md:text-sm" style={{ color: fitventBrandingColor }}>
                  <PoweredByFitvent dark={pageIsDark} />
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main>{children}</main>

        {/* Footer */}
        {showPoweredBy && (
          <footer className={`mt-12 border-t py-8 ${hasMobileTicketBar ? 'pb-32' : 'pb-8'} lg:pb-8`} style={{ borderColor: 'var(--fv-border)' }}>
            <div className="mx-auto max-w-7xl px-4 text-center text-sm" style={{ color: fitventBrandingColor }}>
              <PoweredByFitvent dark={pageIsDark} />
              <p className="mt-1 text-xs opacity-60" style={{ fontFamily: 'var(--fvd-font-body)' }}>One platform for every fitness event</p>
            </div>
          </footer>
        )}
      </div>
    </div>
    </>
  );
}

/**
 * "Powered by fitvent" with the mark, the same in the header and the footer.
 * The mark takes the brand pack's pair for the ground: cyan and bone on a
 * dark page, teal and ink on a light one.
 */
function PoweredByFitvent({ dark }: { dark: boolean }) {
  return (
    // Our lockup, in our own face: it is not part of the organiser's page, so
    // it must not take the heading or body font they picked (an imported
    // icon font once turned it into a serif fallback).
    <a href="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{ fontFamily: 'var(--fvd-font-body)', minHeight: 36 }}>
      <svg width="18" height="18" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <polygon points="8,14 92,14 92,28 22,28" fill={dark ? '#2ADFDF' : '#006D6D'} />
        <polygon points="34,44 92,44 92,58 48,58" fill={dark ? '#F4FAF8' : '#10201D'} />
        <polygon points="68,74 92,74 92,88 78,88" fill={dark ? '#F4FAF8' : '#10201D'} />
      </svg>
      {/* Beside the mark it is the logo, and the logo is always lowercase,
          set in the wordmark face (brand pack). */}
      <span>
        Powered by{' '}
        <span style={{ fontFamily: 'var(--fvd-font-wordmark)', fontWeight: 700, letterSpacing: '-0.043em' }}>fitvent</span>
      </span>
    </a>
  );
}
